import { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

export default function SmartLinksPage() {
  const [activeTab, setActiveTab] = useState('citation');
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [contentUrl, setContentUrl] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [contentBody, setContentBody] = useState('');
  const [infographicId, setInfographicId] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const errDetail = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || x).join(', ');
    return err.message || 'Request failed';
  };

  const runCitation = async () => {
    setIsLoading(true);
    setStatus('Finding opportunities...');
    try {
      const { data: res } = await api.post('/smart-links/citation-opportunities', { industry, location });
      setStatus('Success!');
      setData(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const runSyndicate = async () => {
    setIsLoading(true);
    setStatus('Syndicating...');
    try {
      const { data: res } = await api.post('/smart-links/syndicate', {
        content_url: contentUrl,
        title: contentTitle,
        body: contentBody,
        platforms: ['medium', 'linkedin_article', 'devto'],
      });
      setStatus('Success!');
      setData(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  const runInfographic = async () => {
    setIsLoading(true);
    const id = infographicId.trim() || 'default';
    setStatus('Building distribution kit...');
    try {
      const { data: res } = await api.post(`/smart-links/distribute-infographic/${encodeURIComponent(id)}`);
      setStatus('Success!');
      setData(res);
    } catch (error) {
      setStatus(`Error: ${errDetail(error)}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div className="space-y-6 p-4 md:p-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Smart Link Building</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">Citations, syndication, and infographic distribution assets.</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-800">
        {[
          { id: 'citation', label: 'Citation Builder' },
          { id: 'syndication', label: 'Content Syndication' },
          { id: 'infographic', label: 'Infographic Distribution' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`pb-2 px-2 text-sm ${activeTab === t.id ? 'border-b-2 border-violet-600 font-medium text-violet-600' : 'text-slate-500'}`}
            onClick={() => {
              setActiveTab(t.id);
              setData(null);
              setStatus('');
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4">
        {activeTab === 'citation' && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Industry</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="Plumbing"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="Chicago"
              />
            </div>
            <button
              type="button"
              onClick={runCitation}
              disabled={isLoading}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Finding…' : 'Find Opportunities'}
            </button>
          </>
        )}

        {activeTab === 'syndication' && (
          <>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Content URL</label>
              <input
                type="url"
                value={contentUrl}
                onChange={(e) => setContentUrl(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="https://example.com/blog/post"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
              <input
                type="text"
                value={contentTitle}
                onChange={(e) => setContentTitle(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="How we doubled organic traffic in 90 days"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Body</label>
              <textarea
                value={contentBody}
                onChange={(e) => setContentBody(e.target.value)}
                rows={6}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                placeholder="Paste the article body here. The service will adapt it per platform."
              />
            </div>
            <button
              type="button"
              onClick={runSyndicate}
              disabled={isLoading}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Syndicating…' : 'Syndicate Content'}
            </button>
          </>
        )}

        {activeTab === 'infographic' && (
          <>
            <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2 mb-2">
              Paste the ID shown under <strong className="font-medium text-slate-600 dark:text-slate-300">Images &amp; Infographics</strong> → Infographics (starts with <code className="text-[11px] bg-slate-100 dark:bg-slate-800 px-1 rounded">inf_</code>). Leave blank to use the demo default.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Infographic ID (optional)</label>
              <input
                type="text"
                value={infographicId}
                onChange={(e) => setInfographicId(e.target.value)}
                className="w-full p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-mono"
                placeholder="e.g. inf_a1b2c3d4e5f6"
              />
            </div>
            <button
              type="button"
              onClick={runInfographic}
              disabled={isLoading}
              className="flex items-center gap-2 bg-violet-600 text-white px-4 py-2 rounded-md text-sm hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isLoading ? 'Distributing…' : 'Distribute Infographic'}
            </button>
          </>
        )}

        {status && (
          <div className={`p-3 rounded-md text-sm ${status.startsWith('Error') ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300' : 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200'}`}>
            {status}
          </div>
        )}

        {data && (
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-3">Results</h3>

            {/* error */}
            {data.success === false && (
              <div className="p-4 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                <span className="font-semibold">Error: </span>{data.error}
              </div>
            )}

            {/* citation opportunities */}
            {Array.isArray(data.opportunities) && (
              <ul className="space-y-2">
                {data.opportunities.map((opp, i) => (
                  <li key={i} className="rounded-md border border-slate-200 dark:border-slate-700 p-3 text-xs space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      {opp.name && <p className="font-semibold text-slate-800 dark:text-slate-100">{opp.name}</p>}
                      {opp.relevance_score != null && (
                        <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-[10px]">
                          {opp.relevance_score}/100 relevance
                        </span>
                      )}
                    </div>
                    {opp.type && <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">{opp.type.replace(/_/g, ' ')}</span>}
                    {opp.url && <a href={opp.url} target="_blank" rel="noopener noreferrer" className="block text-violet-600 dark:text-violet-400 hover:underline break-all">{opp.url}</a>}
                    {opp.why && <p className="text-slate-600 dark:text-slate-400">{opp.why}</p>}
                    {opp.submission_content && (
                      <p className="text-slate-500 dark:text-slate-500 italic border-l-2 border-slate-200 dark:border-slate-700 pl-2">{opp.submission_content}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* syndication results */}
            {Array.isArray(data.syndicated_drafts) && (
              <ul className="space-y-2">
                {data.syndicated_drafts.map((p, i) => (
                  <li key={i} className="p-3 rounded-md border border-slate-200 dark:border-slate-700 text-xs space-y-1">
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.status === 'draft_created' ? 'bg-green-500' : 'bg-red-400'}`} />
                      <span className="font-medium text-slate-700 dark:text-slate-200">{p.platform_name || p.platform}</span>
                      <span className="text-slate-500 dark:text-slate-400">{p.status}{p.char_count ? ` · ${p.char_count} chars` : ''}</span>
                      {p.submit_url && (
                        <a href={p.submit_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-violet-600 dark:text-violet-400 hover:underline">
                          Open {p.platform}
                        </a>
                      )}
                    </div>
                    {p.error && <p className="text-red-600 dark:text-red-400">{p.error}</p>}
                    {p.content && (
                      <pre className="whitespace-pre-wrap text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 p-2 rounded">{p.content}</pre>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* infographic distribution assets */}
            {data.distribution_assets && (
              <div className="space-y-3">
                {data.distribution_assets.embed_code && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Embed code</p>
                    <pre className="text-xs whitespace-pre-wrap break-all bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 rounded">{data.distribution_assets.embed_code}</pre>
                  </div>
                )}
                {data.distribution_assets.outreach_template && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Outreach template</p>
                    <pre className="text-xs whitespace-pre-wrap bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 rounded">{data.distribution_assets.outreach_template}</pre>
                  </div>
                )}
                {data.distribution_assets.social_media_caption && (
                  <div>
                    <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Social caption</p>
                    <pre className="text-xs whitespace-pre-wrap bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-2 rounded">{data.distribution_assets.social_media_caption}</pre>
                  </div>
                )}
              </div>
            )}

            {/* generic key-value fallback */}
            {data.success !== false && !data.opportunities && !data.syndicated_drafts && !data.distribution_assets && (
              <dl className="divide-y divide-slate-100 dark:divide-slate-800">
                {Object.entries(data).map(([k, v]) => (
                  <div key={k} className="flex gap-3 py-1.5">
                    <dt className="text-xs text-slate-500 dark:text-slate-400 w-36 flex-shrink-0">{k}</dt>
                    <dd className="text-xs text-slate-800 dark:text-slate-200 break-all">
                      {typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
